import React, { useRef, useEffect, useState } from 'react';
import { Box, Typography, Alert, Paper, CircularProgress } from '@mui/material';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

interface MapboxTestProps {
  className?: string;
}

const MapboxTestComponent: React.FC<MapboxTestProps> = ({ className }) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  useEffect(() => {
    // Set Mapbox access token
    const accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
    
    if (!accessToken) {
      setError('Mapbox access token is not configured');
      setIsLoading(false);
      return;
    }

    mapboxgl.accessToken = accessToken;

    if (map.current) return; // Initialize map only once

    try {
      if (!mapContainer.current) {
        setError('Map container not found');
        setIsLoading(false);
        return;
      }

      // Initialize Mapbox map
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v12', // Basic street map style
        center: [-98.5795, 39.8282], // Center of USA
        zoom: 4,
        attributionControl: true
      });

      // Add map event listeners
      map.current.on('load', () => {
        console.log('Mapbox map loaded successfully');
        setMapLoaded(true);
        setIsLoading(false);
        
        // Add a test marker
        if (map.current) {
          new mapboxgl.Marker()
            .setLngLat([-98.5795, 39.8282])
            .setPopup(new mapboxgl.Popup().setHTML('<h3>Mapbox Test</h3><p>Map is working correctly!</p>'))
            .addTo(map.current);
        }
      });

      map.current.on('error', (e) => {
        console.error('Mapbox error:', e);
        setError(`Mapbox error: ${e.error?.message || 'Unknown error'}`);
        setIsLoading(false);
      });

    } catch (err) {
      console.error('Failed to initialize Mapbox:', err);
      setError(`Failed to initialize Mapbox: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setIsLoading(false);
    }

    // Cleanup function
    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  return (
    <Paper sx={{ p: 3 }} className={className}>
      <Typography variant="h5" gutterBottom>
        Mapbox Integration Test
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Testing Mapbox GL JS integration with API key validation
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Error:</Typography>
          {error}
        </Alert>
      )}

      {isLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <CircularProgress size={20} />
          <Typography>Loading Mapbox map...</Typography>
        </Box>
      )}

      {mapLoaded && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Mapbox map loaded successfully! API key is valid.
        </Alert>
      )}

      <Box
        ref={mapContainer}
        sx={{
          width: '100%',
          height: 400,
          borderRadius: 1,
          border: 1,
          borderColor: 'divider',
          overflow: 'hidden'
        }}
      />

      <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
        API Token: {process.env.REACT_APP_MAPBOX_ACCESS_TOKEN ? 
          `${process.env.REACT_APP_MAPBOX_ACCESS_TOKEN.substring(0, 20)}...` : 
          'Not configured'
        }
      </Typography>
    </Paper>
  );
};

export default MapboxTestComponent;